import { Visibility, VisibilityOff } from "@mui/icons-material"
import {
    Alert,
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { Field, Form, Formik, FormikHelpers } from "formik"
import { useState } from "react"
import * as yup from "yup"
import { BackButton } from "../../../shared/components/buttons/BackButton"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { createPassword, getValidatePwRequest, validatePassword } from "../loginApi"
import { useNavigate } from "react-router-dom"
import { ROOT_ROUTE } from "../../../constants/routes"
import { AuthenticationUser, isAuthenticationComplete } from "../loginTypes"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { useAppDispatch } from "../../store/storeHooks"
import { login } from "../loginSlice"
import { useLocationState } from "../../../core/router/routerHooks"
import { makeStyles } from "tss-react/mui"
import { StandardLoadingButton } from "../../../shared/components/buttons/LoadingButton"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../shared/components/dialogs/StandardErrorDialog"

export type CreatePasswordState = Readonly<{
    user: AuthenticationUser | undefined
    oneTimeToken: string
    redirectUrl?: string
}>

const useStyles = makeStyles()((theme) => ({
    formContainer: {
        maxWidth: "25rem",
        width: "100%",
        margin: "auto",
        [theme.breakpoints.down(800)]: {
            flexDirection: "column",
            margin: "0 auto",
        },
    },
    ruleBox: {
        width: "100%",
        marginTop: "1.75rem",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.tailwind.sky[900],
        padding: "1rem",
        listStyleType: "disc",
        listStylePosition: "inside",
    },
    errorItem: {
        padding: 0,
        display: "list-item",
    },
    passInput: {
        marginBottom: 0,
        backgroundColor: "red",
    },
}))

type FormValues = {
    newPass: string
    confirmPass: string
}

type CreatePasswordProps = Readonly<{
    isDefault?: boolean
}>

export const CreatePassword = ({ isDefault = true }: CreatePasswordProps) => {
    const { classes } = useStyles()

    const [showPassword, setShowPassword] = useState(false)
    const [showErrorToaster, setShowErrorToaster] = useState(false)
    const [errorMessage, setErrorMessage] = useState("Something went wrong")
    const [pwErrors, setPwErrors] = useState<string[]>(["at least one number", "at least one number"])

    const { errorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()

    const { user, oneTimeToken, redirectUrl } = useLocationState<CreatePasswordState>({
        user: undefined,
        oneTimeToken: "",
        redirectUrl: "",
    })
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()
    const dispatch = useAppDispatch()

    const { mutateAsync: callValidatePassword } = usePublicApiMutation(validatePassword, {})
    const { mutateAsync: callCreatePassword } = usePublicApiMutation(createPassword, {})

    const onSubmit = async ({ confirmPass }: FormValues, _helpers: FormikHelpers<FormValues>) => {
        if (!oneTimeToken) {
            openErrorDialog({
                title: l10n.getString("create-password-missing-token-title"),
                description: l10n.getString("create-password-missing-token-description"),
            })
            return
        }

        const response = await callCreatePassword({
            oneTimeToken,
            password: confirmPass,
        })

        if (isAuthenticationComplete(response)) {
            dispatch(
                login({
                    authToken: response.authentication.token,
                    user: response.user,
                }),
            )
            if (redirectUrl) {
                navigate(redirectUrl)
            } else {
                navigate(ROOT_ROUTE)
            }
        } else {
            setShowErrorToaster(true)
            setErrorMessage(
                `Client expected authentication complete but server said: ${response.authenticationResult.action}`,
            )
        }
    }

    const initialValues = {
        newPass: "",
        confirmPass: "",
    }

    const validationSchema = yup.object({
        newPass: yup
            .string()
            .required(l10n.getString("create-password-required"))
            .test({
                name: "is-pw-valid",
                test: async (value, _context) => {
                    if (!user) return true
                    const request = getValidatePwRequest({
                        userType: user.userType,
                        companyId: user.companyId,
                        serviceCenterId: user.serviceCenterId ?? null,
                        userGroupId: user.userGroupId ?? null,
                        password: value,
                    })
                    const response = await callValidatePassword(request)
                    if (response && !response.validator.success) {
                        setPwErrors(response.validator.rules)
                        return false
                    } else {
                        setPwErrors([])
                        return true
                    }
                },
                message: l10n.getString("create-password-password-invalid-for-policy"),
            }),
        confirmPass: yup
            .string()
            .required(l10n.getString("create-password-new-required"))
            .oneOf([yup.ref("newPass")], l10n.getString("create-password-matching")),
    })

    return (
        <Box className={classes.formContainer}>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ errors, touched, isValid, isSubmitting }) => (
                    <Box component={Form}>
                        <LocalizedStrict id={isDefault ? "create-password-title" : "activation-account-password-title"}>
                            <Typography variant="h5" align="left" style={{ marginBottom: "16px" }}>
                                {isDefault ? "New Password" : "Create password"}
                            </Typography>
                        </LocalizedStrict>

                        <LocalizedStrict
                            id={
                                isDefault
                                    ? "create-password-new-password-field"
                                    : "activation-account-password-new-password-field"
                            }
                            attrs={{ label: true }}
                        >
                            <Field
                                as={TextField}
                                type={showPassword ? "text" : "password"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                name="newPass"
                                label={isDefault ? "New Password" : "Enter password"}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={touched.newPass && errors.newPass}
                                helperText={touched.newPass && errors.newPass}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict id="create-password-confirm-password-field" attrs={{ label: true }}>
                            <Field
                                as={TextField}
                                type={showPassword ? "text" : "password"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                name="confirmPass"
                                label="Confirm Password"
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={touched.confirmPass && errors.confirmPass}
                                helperText={touched.confirmPass && errors.confirmPass}
                            />
                        </LocalizedStrict>
                        <Stack
                            flexDirection={isDefault ? "column" : "row-reverse"}
                            gap={isDefault ? undefined : "5rem"}
                            marginTop="1rem"
                        >
                            <LocalizedStrict
                                id={isDefault ? "create-password-submit-button" : "activation-account-password-button"}
                            >
                                <StandardLoadingButton
                                    loading={isSubmitting}
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="black"
                                    disabled={!isValid}
                                    sx={isDefault ? { marginTop: 3, marginBottom: 2 } : { marginBottom: 2 }}
                                >
                                    {isDefault ? "Reset Password" : "Create password"}
                                </StandardLoadingButton>
                            </LocalizedStrict>
                            <BackButton />
                        </Stack>
                        {pwErrors.length > 0 && (
                            <List className={classes.ruleBox}>
                                {pwErrors.map((message) => (
                                    <ListItem className={classes.errorItem}>{message}</ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                )}
            </Formik>

            <Snackbar
                open={showErrorToaster}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={5000}
                onClose={() => setShowErrorToaster(false)}
            >
                <Alert
                    onClose={() => setShowErrorToaster(false)}
                    sx={{ marginBottom: "2rem", marginRight: "1rem" }}
                    severity="error"
                    variant="filled"
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
            <StandardErrorDialog dialogDetails={errorDialog} onClose={() => closeErrorDialog()} />
        </Box>
    )
}
